import { MigrationSpec } from "../migration.models"

/**
 * Mirrors `LEGACY_TRANSLATION_KEYS` in api-v2's
 * `Message/drivers/livechat/livechatAccount.mongo.driver.ts`. `translations` is a Mixed blob with
 * one object per language a tenant enabled, so no fixed dotted path can find them all.
 */
const LEGACY_TRANSLATION_KEYS = [
  "greetingMessage1",
  "greetingMessage2",
  "welcomeMessageDuringWorkingHoursMessage1",
  "welcomeMessageDuringWorkingHoursMessage2",
  "welcomeMessageOutsideOfWorkingHoursMessage1",
  "welcomeMessageOutsideOfWorkingHoursMessage2"
]

const LEGACY_WELCOME_PATHS = [
  "getStartedInfo.greetingMessage",
  "getStartedInfo.welcomeMessageDuringWorkHours",
  "getStartedInfo.welcomeMessageOutsideWorkHours"
]

const liveChatWelcomeMessages: MigrationSpec = {
  type: "REMOVE_LIVE_CHAT_WELCOME_MESSAGES",
  collection: "LiveChatAccounts",
  risk: "DESTRUCTIVE",
  title: {
    en: "LiveChat greeting & welcome messages → FlowBot",
    tr: "LiveChat karşılama ve hoş geldin mesajları → FlowBot"
  },
  description: {
    en:
      "Unsets `getStartedInfo.greetingMessage`, `welcomeMessageDuringWorkHours` and " +
      "`welcomeMessageOutsideWorkHours`, and removes the six translation rows that carried " +
      "their text from every language on the account. FlowBot owns these messages now.",
    tr:
      "`getStartedInfo.greetingMessage`, `welcomeMessageDuringWorkHours` ve " +
      "`welcomeMessageOutsideWorkHours` alanlarını siler; metinlerini taşıyan altı çeviri " +
      "satırını hesaptaki her dilden kaldırır. Bu mesajlar artık FlowBot'ta yönetiliyor."
  },
  // Copied literally from LEGACY_WELCOME_MESSAGE_FILTER in the api-v2 driver.
  affectedFilter: {
    $or: [
      ...LEGACY_WELCOME_PATHS.map((path) => ({ [path]: { $exists: true } })),
      {
        $expr: {
          $anyElementTrue: {
            $map: {
              input: { $objectToArray: { $ifNull: ["$translations", {}] } },
              as: "language",
              in: {
                $cond: [
                  { $eq: [{ $type: "$$language.v" }, "object"] },
                  {
                    $anyElementTrue: {
                      $map: {
                        input: { $objectToArray: "$$language.v" },
                        as: "entry",
                        in: { $in: ["$$entry.k", LEGACY_TRANSLATION_KEYS] }
                      }
                    }
                  },
                  false
                ]
              }
            }
          }
        }
      }
    ]
  },
  sampleFields: [
    "customer",
    "getStartedInfo.greetingMessage",
    "getStartedInfo.welcomeMessageDuringWorkHours",
    "getStartedInfo.welcomeMessageOutsideWorkHours",
    "translations.en.greetingMessage1",
    "translations.tr.greetingMessage1"
  ],
  preview: (doc) => {
    const next = { ...doc }
    delete (next.getStartedInfo as Record<string, unknown> | undefined)
      ?.greetingMessage
    delete (next.getStartedInfo as Record<string, unknown> | undefined)
      ?.welcomeMessageDuringWorkHours
    delete (next.getStartedInfo as Record<string, unknown> | undefined)
      ?.welcomeMessageOutsideWorkHours
    const translations = next.translations as
      | Record<string, Record<string, unknown>>
      | undefined
    if (translations) {
      next.translations = Object.fromEntries(
        Object.entries(translations).map(([language, rows]) => [
          language,
          rows && typeof rows === "object"
            ? Object.fromEntries(
                Object.entries(rows).filter(
                  ([key]) => !LEGACY_TRANSLATION_KEYS.includes(key)
                )
              )
            : rows
        ])
      )
    }
    return next
  },
  notes: [
    {
      en:
        "The text is deleted, not archived — a tenant who still wants a greeting has to rebuild " +
        "it as a FlowBot welcome message.",
      tr:
        "Metinler arşivlenmez, silinir — karşılama mesajı isteyen müşterinin bunu FlowBot " +
        "hoş geldin mesajı olarak yeniden kurması gerekir."
    },
    {
      en:
        "Deploy api-v2, user-panel-v2 and the LiveChat widget bundle before running this: the " +
        "old widget reads `getStartedInfo.greetingMessage.firstMessage` without a null guard.",
      tr:
        "Çalıştırmadan önce api-v2, user-panel-v2 ve LiveChat widget paketini deploy edin: " +
        "eski widget `getStartedInfo.greetingMessage.firstMessage` alanını korumasız okuyor."
    }
  ]
}

export default liveChatWelcomeMessages
